import { useRef } from 'react';
import { useSelector } from 'react-redux';
import HighchartsReact from 'highcharts-react-official';
import { useGoalsTranslations } from 'modules/iterableGoals/hooks';
import { METRIC_SECTIONS_ORDER, REVENUE_SECTIONS } from 'modules/iterableGoals/constants';
import {
  TabSectionPropType,
  GoalAnalyticsMetricKey,
  PerformanceMetrics,
} from 'modules/iterableGoals/types';
import { getGoalPerformanceMetrics } from 'modules/iterableGoals/ducks/selectors';
import { useQueryParamContext, useReportingContext } from 'modules/iterableGoals/contexts';
import { selectIsIterableGoalsStretchFlagOn } from 'core/userContext';
import { SectionTitle } from '../../SectionTitle';
import { AnalyticsCard } from '../AnalyticsCard';
import { StyledDivider } from '../Styled';
import {
  GoalPerformanceWrapper,
  GoalPerformanceMetricsWrapper,
  GoalPerformanceGraphSectionWrapper,
  GoalPerformanceGraphHeader,
} from './Styled';
import { GoalPerformanceChart } from './GoalPerformanceChart';
import { ReportingToggle } from '../../ReportingToggle';
import { PerformanceReportingCard } from './PerformanceReportingCard';

const GoalPerformanceMetrics = ({
  metrics,
  previousMetrics,
}: {
  metrics: PerformanceMetrics;
  previousMetrics?: PerformanceMetrics;
}) => {
  const { t } = useGoalsTranslations();

  return (
    <GoalPerformanceMetricsWrapper>
      {METRIC_SECTIONS_ORDER.map(([key, label], idx) => {
        const { title, infoText } = t<string, Record<string, string>>(label, {
          returnObjects: true,
        });
        const value = metrics?.[key] ?? 0;
        const previousValue = previousMetrics?.[key] ?? 0;

        return (
          <PerformanceReportingCard
            key={`metric-section-${idx}`}
            valueKey={key}
            title={title}
            value={value}
            previousValue={previousValue}
            infoText={infoText}
          />
        );
      })}
    </GoalPerformanceMetricsWrapper>
  );
};

const GoalPerformanceRevenue = ({
  revenueData,
  previousRevenue,
}: {
  revenueData: PerformanceMetrics;
  previousRevenue: PerformanceMetrics;
}) => {
  const { t } = useGoalsTranslations();

  return (
    <GoalPerformanceMetricsWrapper>
      {REVENUE_SECTIONS.map(([key, label], idx) => {
        const { title, infoText } = t<string, Record<string, string>>(label, {
          returnObjects: true,
        });
        const value = revenueData?.[key];
        const previousValue = previousRevenue?.[key] ?? 0;

        return (
          <PerformanceReportingCard
            key={`metric-section-${idx}`}
            valueKey={key}
            title={title}
            value={value}
            isCurrency
            previousValue={previousValue}
            infoText={infoText}
          />
        );
      })}
    </GoalPerformanceMetricsWrapper>
  );
};

export const GoalPerformance = ({
  loading,
  error,
  reFetchGoalAnalyticsData,
}: TabSectionPropType) => {
  const { t } = useGoalsTranslations();
  const isIterableGoalsStretchFlagOn = useSelector(selectIsIterableGoalsStretchFlagOn);
  const chartRef = useRef<HighchartsReact>(null);
  const { metrics, revenue, previousMetrics, previousRevenue } =
    useSelector(getGoalPerformanceMetrics) || {};
  const { graphData: metricsGraphData, ...metricData } = metrics;
  const { graphData: revenueGraphData, ...revenueData } = revenue;
  const { includeRevenue } = useQueryParamContext();
  const { isRevenue, getReportingTranslation } = useReportingContext();
  const sectionComponent =
    includeRevenue && isIterableGoalsStretchFlagOn ? <ReportingToggle /> : undefined;
  const performanceCards = isRevenue ? (
    <GoalPerformanceRevenue revenueData={revenueData} previousRevenue={previousRevenue} />
  ) : (
    <GoalPerformanceMetrics metrics={metricData} previousMetrics={previousMetrics} />
  );

  return (
    <>
      <SectionTitle
        title={t('title.analytics.goalPerformance')}
        subTitle={t('title.analytics.campaignSend', {
          count: metricData[GoalAnalyticsMetricKey.TOTAL_SENT],
        })}
        sectionComponent={sectionComponent}
      />
      <AnalyticsCard
        height={660}
        loading={loading}
        error={error}
        noPadding={true}
        handleReload={reFetchGoalAnalyticsData}
      >
        <GoalPerformanceWrapper>
          {performanceCards}
          <StyledDivider />
          <GoalPerformanceGraphSectionWrapper>
            <GoalPerformanceGraphHeader>
              {getReportingTranslation('goalPerformance.goalConversionsOverTime')}
            </GoalPerformanceGraphHeader>
            <GoalPerformanceChart
              graphData={isRevenue ? revenueGraphData : metricsGraphData}
              innerRef={chartRef}
            />
          </GoalPerformanceGraphSectionWrapper>
        </GoalPerformanceWrapper>
      </AnalyticsCard>
    </>
  );
};
